import { type FC, useMemo } from 'react';
import { Modal, Table } from 'react-bootstrap';

import useCreateShareRequest from '../../../../hooks/api/sbomShare/useCreateShareRequest';
import useGetSharedOrganizations from '../../../../hooks/api/sbomShare/useGetSharedOrganizations';
import useGetShareRequests from '../../../../hooks/api/sbomShare/useGetShareRequests';
import Button from '../../../generic/Button/Button';
import { BUTTON_SIZE, BUTTON_TYPE } from '../../../generic/Button/button-constants';
import TooltipButton from '../../../generic/Button/TooltipButton';
import Loader from '../../../generic/Loader';
import { useAddJobToNotificationQueue } from '../../../JobNotificationHub/Provider';
import type {
  SharedOrganizationResponse,
  ShareRequestTaskResponse,
} from '../../../../../shared/types/routes/sbomShare';

import styles from './ShareProjectModal.module.less';

type ShareProjectModalProps = {
  isVisible: boolean;
  onHide: () => void;
  projectLocator: string;
  revisionLocator?: string;
  revisionUpdatedAt?: string;
};

type ShareRequestOrg = {
  sharedOrganizationId: number;
  receivingOrganizationName: string;
  sharedAt?: string;
};

// Component for organizations that haven't been shared with yet
const NewShareTable: FC<{
  organizations?: SharedOrganizationResponse[];
  onShare: (orgId: number) => void;
  isSharing: boolean;
}> = ({ organizations, onShare, isSharing }) => {
  if (!organizations?.length) {
    return null;
  }

  return (
    <>
      <span className={styles.sectionTitle}>Share with:</span>
      <Table hover responsive className={styles.organizationsTable}>
        <tbody>
          {organizations.map((org) => (
            <tr key={org.sharedOrganizationId}>
              <td>
                <span className={styles.organizationName}>{org.receivingOrganizationName}</span>
              </td>
              <td className="text-right">
                <Button
                  type={BUTTON_TYPE.PRIMARY}
                  size={BUTTON_SIZE.SMALL}
                  onClick={() => onShare(org.sharedOrganizationId)}
                  isDisabled={isSharing}
                >
                  Share
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    </>
  );
};

// Component for organizations that have already been shared with
const PreviousShareTable: FC<{
  organizations?: ShareRequestOrg[];
  onShare: (orgId: number) => void;
  revisionUpdatedAt?: string;
}> = ({ organizations, onShare, revisionUpdatedAt }) => {
  if (!organizations?.length) {
    return null;
  }

  return (
    <>
      <span className={styles.sectionTitle}>Shared:</span>
      <Table hover responsive className={styles.organizationsTable}>
        <tbody>
          {organizations.map((org) => (
            <tr key={org.sharedOrganizationId}>
              <td>
                <span className={styles.organizationName}>{org.receivingOrganizationName}</span>
                {org.sharedAt && (
                  <>
                    <br />
                    Last Shared: {new Date(org.sharedAt).toLocaleString()}
                  </>
                )}
              </td>
              <td className="text-right">
                <TooltipButton
                  id={`share-project-modal-send-update-${org.sharedOrganizationId}`}
                  type={BUTTON_TYPE.PRIMARY}
                  size={BUTTON_SIZE.SMALL}
                  onClick={() => onShare(org.sharedOrganizationId)}
                  isDisabled={
                    !!org.sharedAt &&
                    !!revisionUpdatedAt &&
                    new Date(org.sharedAt) >= new Date(revisionUpdatedAt)
                  }
                  showTooltip={
                    !!org.sharedAt &&
                    !!revisionUpdatedAt &&
                    new Date(org.sharedAt) >= new Date(revisionUpdatedAt)
                  }
                  tooltip="This revision has already been shared with this organization"
                >
                  Send Update
                </TooltipButton>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    </>
  );
};

// Main modal content component
const ModalContent: FC<{
  isLoading: boolean;
  newShareRequests?: SharedOrganizationResponse[];
  previousShareRequests?: ShareRequestOrg[];
  onShare: (orgId: number) => void;
  isSharing: boolean;
  revisionUpdatedAt?: string;
}> = ({ isLoading, newShareRequests, previousShareRequests, onShare, isSharing, revisionUpdatedAt }) => {
  if (isLoading) {
    return <Loader />;
  }

  return (
    <>
      <h5>You can share this project with connected organizations.</h5>
      <br />
      <NewShareTable
        organizations={newShareRequests}
        onShare={onShare}
        isSharing={isSharing}
      />
      <PreviousShareTable
        organizations={previousShareRequests}
        onShare={onShare}
        revisionUpdatedAt={revisionUpdatedAt}
      />
    </>
  );
};

const ShareProjectModalInternal: FC<ShareProjectModalProps> = ({
  isVisible,
  onHide,
  projectLocator,
  revisionLocator,
  revisionUpdatedAt,
}) => {
  const addJob = useAddJobToNotificationQueue();

  // Fetch organizations available for sharing
  const {
    data: organizationsData,
    isLoading: isLoadingOrganizations,
  } = useGetSharedOrganizations();

  // Create share request mutation
  const {
    mutate: createShareRequest,
    isLoading: isCreatingShareRequest,
  } = useCreateShareRequest(projectLocator);

  const {
    data: shareRequestsData,
    isLoading: isLoadingShareRequests,
  } = useGetShareRequests(projectLocator);

  // Handle sharing submission for a specific organization
  const handleShare = (orgId: number) => {
    if (!revisionLocator) {
      return; // Don't proceed if missing required data
    }

    createShareRequest({
      revisionId: revisionLocator,
      sharedOrganizationId: orgId,
    }, {
      onSuccess: (response: ShareRequestTaskResponse | undefined) => {
        if (response) {
          addJob(response.task.jobToken, response.task.name);
        }
        onHide();
      },
    });
  };

  const [newShareRequests, previousShareRequests] = useMemo(() => {
    const shareRequestMap = new Map(
      shareRequestsData?.shareRequests
        .filter((request) => request.originRevisionId === revisionLocator)
        // We can have multiple share requests, so we need to sort them in ascending order
        // so that only the most recent share request enters the map
        .sort((a, b) => new Date(a.sharedAt).getTime() - new Date(b.sharedAt).getTime())
        .map((request) => [request.sharedOrganizationId, request]),
    );

    const newShareRequestsToReturn: SharedOrganizationResponse[] = [];
    const previousShareRequestsToReturn: ShareRequestOrg[] = [];
    organizationsData?.organizationsToShareWith.forEach((org) => {
      if (shareRequestMap.has(org.sharedOrganizationId)) {
        previousShareRequestsToReturn.push({
          sharedAt: shareRequestMap.get(org.sharedOrganizationId)?.sharedAt,
          sharedOrganizationId: org.sharedOrganizationId,
          receivingOrganizationName: org.receivingOrganizationName,
        });
        return;
      }

      newShareRequestsToReturn.push(org);
    });

    return [newShareRequestsToReturn, previousShareRequestsToReturn];
  }, [shareRequestsData, organizationsData, revisionLocator]);

  return (
    <Modal
      show={isVisible}
      onHide={onHide}
      title="Share Project"
    >
      <Modal.Header closeButton>
        <Modal.Title>Share Project</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <ModalContent
          isLoading={isLoadingOrganizations || isLoadingShareRequests}
          newShareRequests={newShareRequests}
          previousShareRequests={previousShareRequests}
          onShare={handleShare}
          isSharing={isCreatingShareRequest}
          revisionUpdatedAt={revisionUpdatedAt}
        />
      </Modal.Body>
    </Modal>
  );
};

const ShareProjectModal: FC<ShareProjectModalProps> = (props) => {
  if (!props.isVisible) {
    return null;
  }

  // Don't make calls to the API if the modal is not visible,
  // we don't have a way to invalidate the queries because work is done in the job
  // so we need to rely on the query automatically refetching whenever the modal is opened
  /* eslint-disable react/jsx-props-no-spreading */
  return <ShareProjectModalInternal {...props} />;
};

export default ShareProjectModal;
